import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { auditPath } from '../utils/auditRoutes';
import { Button } from '../components/ui/button';
import { PageFrame } from '../components/PageFrame';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { trackAuditStarted } from '../utils/analytics';

export default function CoverPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateContact, updateCompany } = useAudit();
  
  // Load contact info from session on mount
  useEffect(() => {
    const session = localStorage.getItem('audit_session');
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        const contact = sessionData.contact || {};
        
        // Populate context from session
        if (contact.firstName) updateContact('firstName', contact.firstName);
        if (contact.lastName) updateContact('lastName', contact.lastName);
        if (contact.email) updateContact('email', contact.email);
        if (contact.industry) updateContact('industry', contact.industry);
        if (contact.employeeCount) updateContact('employeeCount', contact.employeeCount);
        if (contact.role) updateContact('role', contact.role);
        if (contact.company) updateCompany(contact.company);
        
        // Also set name field for backward compatibility
        const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
        if (fullName) updateContact('name', fullName);
      } catch (e) {
        console.error('Error loading session data:', e);
      }
    }
  }, []);

  return (
    <PageFrame pageNumber={1} backgroundStyle="dark" showLogo={false}>
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'linear-gradient(rgba(4,154,170,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(4,154,170,0.3) 1px, transparent 1px)',
              backgroundSize: '40px 40px'
            }}
          />
          <div className="absolute top-20 right-20 w-64 h-64 bg-accent/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <img 
                src="/ai-opportunity-audit-logo.png" 
                alt="AI Opportunity Audit" 
                className="h-24 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>

            {/* Title */}
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-6"
              data-testid="cover-title"
            >
              AI Opportunity Audit™
            </h1>

            {/* Subtitle */}
            <p className="text-xl sm:text-2xl text-accent font-light mb-12" data-testid="cover-subtitle">
              Find the first safe, useful AI workflow for your firm — without risking client trust.
            </p>

            {/* CTA Button */}
            <Button
              size="lg"
              onClick={() => {
                trackAuditStarted();
                navigate(auditPath('/welcome', location.pathname));
              }}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all gap-3 group"
              data-testid="start-audit-button"
            >
              <Sparkles className="h-5 w-5" />
              Start the AI Opportunity Audit
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="mt-16 text-white/70 text-sm"
            >
              Created by <span className="font-semibold text-white">Trilogy One Consulting</span>
              <div className="mt-2 text-accent text-xs uppercase tracking-wider">Clarity. Momentum. Resilience.</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </PageFrame>
  );
}